import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import { useUserInfoSimple } from '../../hooks/useUserInfoSimple';
import { uploadProfileImage, updateBasicInfo, verifyIdentity, IdentityVerificationRequest } from '../../services/auth/authApi';
import BasicInfoEditModal from '../../components/modals/BasicInfoEditModal';
import defaultProfileImage from '../../assets/images/profile.png';

interface MyPageProfileEditProps {}

const MyPageProfileEdit: React.FC<MyPageProfileEditProps> = () => {
  const { userInfo, loading, refresh } = useUserInfoSimple();
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isEditingBasicInfo, setIsEditingBasicInfo] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [activeVerificationTab, setActiveVerificationTab] = useState<'student' | 'doctor'>('doctor');
  const [verificationStatus] = useState<'verified' | 'pending' | 'none'>('verified'); // verified, pending, none
  const [marketingConsent, setMarketingConsent] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isBasicInfoModalOpen, setIsBasicInfoModalOpen] = useState(false);
  
  // 파일 입력 참조
  const fileInputRef = useRef<HTMLInputElement>(null);
  const identityFileInputRef = useRef<HTMLInputElement>(null);
  
  // 회원 자격 인증 상태
  const [isSubmittingIdentity, setIsSubmittingIdentity] = useState(false);
  const [identityFormData, setIdentityFormData] = useState({
    department: '',
    licenseNumber: '',
    selectedFile: null as File | null
  });

  // 컴포넌트 마운트 시 사용자 정보 로드
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token && !userInfo && !loading) {
      refresh();
    }
  }, []); // 빈 의존성 배열로 마운트 시에만 실행

  // 생년월일 드롭다운 상태
  const [dropdownStates, setDropdownStates] = useState({
    year: false,
    month: false,
    day: false
  });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birth: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    identity: 'DOCTOR',
    university: '',
    medicine: '',
    introduction: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    specialization: '',
    licenseNumber: ''
  });

  // 페이지 로드 시 사용자 정보 새로고침 체크
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    
    if (token && !userInfo && !loading) {
      refresh();
    }
  }, [userInfo, loading, refresh]);

  // 사용자 정보 로드 및 폼 데이터 설정
  useEffect(() => {
    if (userInfo) {
      setIsLoggedIn(true);
      
      // birth 데이터 파싱 (YYYY-MM-DD 형식)
      let birthYear = '2000';
      let birthMonth = '01';
      let birthDay = '01';
      
      if (userInfo.birth) {
        const birthParts = userInfo.birth.split('-');
        if (birthParts.length === 3) {
          birthYear = birthParts[0];
          birthMonth = birthParts[1];
          birthDay = birthParts[2];
        }
      }
      
      // 폰 번호 포맷팅 (01022882418 -> 010-2288-2418)
      let formattedPhone = userInfo.phone || '010-0000-0000';
      if (userInfo.phone && userInfo.phone.length === 11) {
        formattedPhone = `${userInfo.phone.slice(0, 3)}-${userInfo.phone.slice(3, 7)}-${userInfo.phone.slice(7)}`;
      }
      
      console.log('데이터 파싱:', {
        originalBirth: userInfo.birth,
        parsedYear: birthYear,
        parsedMonth: birthMonth,
        parsedDay: birthDay,
        originalPhone: userInfo.phone,
        formattedPhone: formattedPhone
      });
      
      setFormData(prev => ({
        ...prev,
        name: userInfo.name || '김툽닥',
        email: userInfo.email || 'tobedoctorcampus@gmail.com',
        phone: formattedPhone,
        birth: userInfo.birth || '',
        birthYear: birthYear,
        birthMonth: birthMonth,
        birthDay: birthDay,
        identity: userInfo.identity || 'DOCTOR',
        university: userInfo.university || '',
        medicine: userInfo.medicine || '가정의학과',
        introduction: userInfo.introduction || '',
        specialization: userInfo.medicine || '가정의학과',
      }));
    } else if (!loading) {
      setIsLoggedIn(false);
    }
  }, [userInfo, loading]);

  // 프로필 이미지 업로드 함수
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('handleImageUpload 시작');
    
    const file = event.target.files?.[0];
    if (!file) {
      console.log('파일이 선택되지 않음');
      return;
    }

    console.log('선택된 파일:', {
      name: file.name,
      type: file.type,
      size: file.size
    });

    // 파일 타입 검증
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      console.log('지원하지 않는 파일 형식:', file.type);
      alert('JPG, PNG, GIF, WebP 형식의 이미지만 업로드 가능합니다.');
      return;
    }

    // 파일 크기 검증 (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      console.log('파일 크기 초과:', file.size);
      alert('이미지 크기는 5MB 이하만 업로드 가능합니다.');
      return;
    }

    setIsUploadingImage(true);
    try {
      console.log('프로필 이미지 업로드 시작:', file.name);
      
      // uploadProfileImage 함수 호출
      const updatedUserInfo = await uploadProfileImage(file);
      console.log('프로필 이미지 업로드 성공:', updatedUserInfo);
      
      // 사용자 정보 새로고침
      console.log('사용자 정보 새로고침 시작');
      await refresh();
      console.log('사용자 정보 새로고침 완료');
      
      alert('프로필 이미지가 변경되었습니다.');
    } catch (error) {
      console.error('프로필 이미지 업로드 실패 - 전체 에러:', error);
      console.error('에러 메시지:', error instanceof Error ? error.message : '알 수 없는 에러');
      console.error('에러 스택:', error instanceof Error ? error.stack : 'No stack');
      
      const errorMessage = error instanceof Error ? error.message : '프로필 이미지 업로드에 실패했습니다.';
      alert(`업로드 실패: ${errorMessage}`);
    } finally {
      console.log('업로드 프로세스 완료, 로딩 해제');
      setIsUploadingImage(false);
      // 파일 입력 초기화
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // 사용자 정보 로드 및 폼 데이터 설정
  useEffect(() => {
    if (userInfo) {
      setIsLoggedIn(true);
      
      // birth 데이터 파싱 (YYYY-MM-DD 형식)
      let birthYear = '2000';
      let birthMonth = '01';
      let birthDay = '01';
      
      if (userInfo.birth) {
        const birthParts = userInfo.birth.split('-');
        if (birthParts.length === 3) {
          birthYear = birthParts[0];
          birthMonth = birthParts[1];
          birthDay = birthParts[2];
        }
      }
      
      // 폰 번호 포맷팅 (01022882418 -> 010-2288-2418)
      let formattedPhone = userInfo.phone || '010-0000-0000';
      if (userInfo.phone && userInfo.phone.length === 11) {
        formattedPhone = `${userInfo.phone.slice(0, 3)}-${userInfo.phone.slice(3, 7)}-${userInfo.phone.slice(7)}`;
      }
      
      console.log('데이터 파싱:', {
        originalBirth: userInfo.birth,
        parsedYear: birthYear,
        parsedMonth: birthMonth,
        parsedDay: birthDay,
        originalPhone: userInfo.phone,
        formattedPhone: formattedPhone
      });
      
      setFormData(prev => ({
        ...prev,
        name: userInfo.name || '김툽닥',
        email: userInfo.email || 'tobedoctorcampus@gmail.com',
        phone: formattedPhone,
        birth: userInfo.birth || '',
        birthYear: birthYear,
        birthMonth: birthMonth,
        birthDay: birthDay,
        identity: userInfo.identity || 'DOCTOR',
        university: userInfo.university || '',
        medicine: userInfo.medicine || '가정의학과',
        introduction: userInfo.introduction || '',
        specialization: userInfo.medicine || '가정의학과',
      }));
    } else if (!loading) {
      setIsLoggedIn(false);
    }
  }, [userInfo, loading]);

  // 기본정보 수정 완료 핸들러
  const handleBasicInfoUpdate = async (data: { name: string; birth: string; phone: string }) => {
    try {
      console.log('기본정보 수정 시작:', data);
      
      // 폰 번호에서 하이픈 제거
      const phoneNumber = data.phone.replace(/-/g, '');
      
      const updateData = {
        name: data.name,
        birth: data.birth,
        phone: phoneNumber
      };
      
      await updateBasicInfo(updateData);
      console.log('기본정보 수정 성공');
      
      // 사용자 정보 새로고침
      await refresh();
      
      // 모달 닫기
      setIsBasicInfoModalOpen(false);
      
      alert('기본 정보가 수정되었습니다.');
    } catch (error) {
      console.error('기본정보 수정 실패:', error);
      alert(error instanceof Error ? error.message : '기본정보 수정에 실패했습니다.');
    }
  };

  // 프로필 이미지 클릭 핸들러
  const handleProfileImageClick = () => {
    if (isUploadingImage) return;
    fileInputRef.current?.click();
  };

  // 회원 자격 인증 폼 입력 핸들러
  const handleIdentityInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setIdentityFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 회원 자격 인증 파일 업로드 핸들러
  const handleIdentityFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // 파일 타입 및 크기 검증
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        alert('이미지 파일(JPG, PNG, GIF, WebP) 또는 PDF 파일만 업로드 가능합니다.');
        return;
      }
      
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        alert('파일 크기는 10MB 이하만 업로드 가능합니다.');
        return;
      }
      
      setIdentityFormData(prev => ({
        ...prev,
        selectedFile: file
      }));
    }
  };

  // 회원 자격 인증 제출 핸들러
  const handleIdentitySubmit = async () => {
    try {
      setIsSubmittingIdentity(true);
      
      // 입력값 검증
      if (!identityFormData.department) {
        alert(activeVerificationTab === 'student' ? '소속 대학을 선택해주세요.' : '진료과를 선택해주세요.');
        return;
      }
      
      if (activeVerificationTab === 'student' && !identityFormData.selectedFile) {
        alert('학생증 또는 재학증명서 파일을 업로드해주세요.');
        return;
      }
      
      if (activeVerificationTab === 'doctor' && !identityFormData.licenseNumber) {
        alert('면허번호를 입력해주세요.');
        return;
      }
      
      // API 요청 데이터 구성
      const requestData: IdentityVerificationRequest = {
        identity: activeVerificationTab === 'student' ? 'STUDENT' : 'DOCTOR',
        department: identityFormData.department,
      };
      
      if (activeVerificationTab === 'student' && identityFormData.selectedFile) {
        requestData.idCardFile = identityFormData.selectedFile;
      }
      
      if (activeVerificationTab === 'doctor' && identityFormData.licenseNumber) {
        requestData.licenseNumber = identityFormData.licenseNumber;
      }
      
      console.log('회원 자격 인증 요청 데이터:', requestData);
      
      // API 호출
      const response = await verifyIdentity(requestData);
      
      console.log('회원 자격 인증 성공:', response);
      
      // 성공 메시지
      alert('회원 자격 인증 요청이 제출되었습니다. 관리자 확인 후 승인이 완료됩니다.');
      
      // 폼 초기화
      setIdentityFormData({
        department: '',
        licenseNumber: '',
        selectedFile: null
      });
      
      // 파일 입력 초기화
      if (identityFileInputRef.current) {
        identityFileInputRef.current.value = '';
      }
      
      // 사용자 정보 새로고침
      await refresh();
      
    } catch (error) {
      console.error('회원 자격 인증 실패:', error);
      alert(error instanceof Error ? error.message : '회원 자격 인증에 실패했습니다.');
    } finally {
      setIsSubmittingIdentity(false);
    }
  };

  // 회원 자격 인증 탭 변경 핸들러
  const handleVerificationTabChange = (tab: 'student' | 'doctor') => {
    setActiveVerificationTab(tab);
    // 탭 변경 시 폼 데이터 초기화
    setIdentityFormData({
      department: '',
      licenseNumber: '',
      selectedFile: null
    });
    // 파일 입력 초기화
    if (identityFileInputRef.current) {
      identityFileInputRef.current.value = '';
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 드롭다운 관련 함수들
  const handleDropdownToggle = (dropdown: keyof typeof dropdownStates) => {
    if (!isEditingBasicInfo) return;
    setDropdownStates(prev => ({
      ...prev,
      [dropdown]: !prev[dropdown]
    }));
  };

  const handleDropdownSelect = (dropdown: keyof typeof dropdownStates, value: string) => {
    if (dropdown === 'year') {
      setFormData(prev => ({ ...prev, birthYear: value }));
    } else if (dropdown === 'month') {
      setFormData(prev => ({ ...prev, birthMonth: value }));
    } else if (dropdown === 'day') {
      setFormData(prev => ({ ...prev, birthDay: value }));
    }
    
    setDropdownStates(prev => ({ ...prev, [dropdown]: false }));
  };

  // 생년월일 옵션 생성
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const days = Array.from({ length: 31 }, (_, i) => i + 1);

  if (loading) {
    return (
      <MyPageLayout hideFooter={true}>
        <Container>
          <div style={{ padding: '60px 0' }}>
            로딩 중...
          </div>
        </Container>
      </MyPageLayout>
    );
  }



  return (
    <MyPageLayout hideFooter={true}>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>회원 정보 수정</PageTitle>
          </PageHeader>

          {/* 브레드크럼 */}
          <NavigationContainer>
            <BreadcrumbContainer>
              <BreadcrumbItem>홈</BreadcrumbItem>
              <BreadcrumbChevron>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </BreadcrumbChevron>
              <BreadcrumbItem>마이페이지</BreadcrumbItem>
              <BreadcrumbChevron>
                <svg width="8" height="16" viewBox="0 0 8 16" fill="none">
                  <path 
                    d="M1.73 3.07L6.26 7.6L1.73 12.13" 
                    stroke="rgba(55, 56, 60, 0.61)" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
              </BreadcrumbChevron>
              <BreadcrumbItem>회원 정보 수정</BreadcrumbItem>
            </BreadcrumbContainer>
          </NavigationContainer>

          {/* 메인 콘텐츠 */}
          <MainContent>
            {/* 계정 정보 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>계정 정보</SectionTitle>
              </SectionHeader>

              {/* 프로필 이미지 */}
              <ProfileImageSection>
                <ProfileImageContainer>
                  <ProfileImageWrapper>
                    {loading || isUploadingImage ? (
                      <SkeletonCircle />
                    ) : (
                      <ProfileImage 
                        src={userInfo?.image_url || defaultProfileImage} 
                        alt="프로필" 
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = defaultProfileImage;
                        }}
                      />
                    )}
                    <ProfileImageOverlay className="overlay">
                      {/* 호버 시 표시되는 오버레이 */}
                    </ProfileImageOverlay>
                  </ProfileImageWrapper>
                  
                  {/* 편집 버튼 - 프로필 이미지 밖으로 완전히 분리 */}
                  <EditIconButton onClick={handleProfileImageClick} disabled={isUploadingImage}>
                    {isUploadingImage ? (
                      <UploadSpinner />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd" d="M10.6588 2.54719C11.43 1.77594 12.6805 1.77594 13.4517 2.54719C14.223 3.31845 14.223 4.56891 13.4517 5.34017L5.0351 13.7568C4.92258 13.8694 4.76997 13.9326 4.61084 13.9326H2.6664C2.33503 13.9326 2.06641 13.6639 2.06641 13.3326V11.3881C2.06641 11.229 2.12962 11.0764 2.24214 10.9639L10.6588 2.54719ZM12.6032 3.39572C12.3006 3.09309 11.8099 3.09309 11.5073 3.39572L3.2664 11.6367V12.7326H4.36231L12.6032 4.49164C12.9058 4.18901 12.9058 3.69835 12.6032 3.39572Z" fill="white"/>
                      </svg>
                    )}
                  </EditIconButton>
                </ProfileImageContainer>
                
                {/* 숨겨진 파일 입력 */}
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                />
              </ProfileImageSection>

              {/* 이메일 */}
              <FormGroup>
                <FormLabel>이메일</FormLabel>
                {loading ? (
                  <SkeletonInput />
                ) : (
                  <DisabledInput value={formData.email} readOnly />
                )}
              </FormGroup>

              {/* 비밀번호 */}
              <FormRow>
                <FormGroup style={{ flex: 1 }}>
                  <FormLabel>비밀번호</FormLabel>
                  {loading ? (
                    <SkeletonInput />
                  ) : (
                    <DisabledInput value="********" readOnly />
                  )}
                </FormGroup>
                {loading ? (
                  <SkeletonButton />
                ) : (
                  <ChangeButton onClick={() => setIsChangingPassword(!isChangingPassword)}>
                    변경하기
                  </ChangeButton>
                )}
              </FormRow>

              {/* 비밀번호 변경 폼 */}
              {isChangingPassword && (
                <PasswordChangeSection>
                  <FormGroup>
                    <FormLabel>비밀번호 변경</FormLabel>
                    <FormInput
                      type="password"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      placeholder="현재 비밀번호를 입력해주세요."
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormInput
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      placeholder="새로운 비밀번호를 입력해주세요."
                    />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormInput
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="비밀번호를 다시 입력해주세요."
                    />
                  </FormGroup>
                  
                  <PasswordHint>
                    영문 대소문자, 숫자를 조합하여 8자 이상 16자 이하로 입력해주세요.
                  </PasswordHint>
                  
                  <PasswordChangeButton
                    active={!!(formData.currentPassword && formData.newPassword && formData.confirmPassword)}
                    disabled={!formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
                  >
                    비밀번호 변경하기
                  </PasswordChangeButton>
                </PasswordChangeSection>
              )}
            </Section>

            <Divider />

            {/* 기본 정보 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>기본 정보</SectionTitle>
                {loading ? (
                  <SkeletonButton />
                ) : (
                  <EditButton 
                    active={false}
                    onClick={() => setIsBasicInfoModalOpen(true)}
                  >
                    수정하기
                  </EditButton>
                )}
              </SectionHeader>

              {/* 이름 */}
              <FormGroup>
                <FormLabel>이름</FormLabel>
                {loading ? (
                  <SkeletonInput />
                ) : isEditingBasicInfo ? (
                  <FormInput
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                ) : (
                  <DisabledInput value={formData.name} readOnly />
                )}
              </FormGroup>

              {/* 생년월일 */}
              <FormGroup>
                <FormLabel>생년월일</FormLabel>
                <BirthContainer>
                  {loading ? (
                    <>
                      <SkeletonBirthInput />
                      <SkeletonBirthInput />
                      <SkeletonBirthInput />
                    </>
                  ) : isEditingBasicInfo ? (
                    <>
                      <DropdownContainer>
                        <DropdownButton 
                          type="button"
                          onClick={() => handleDropdownToggle('year')}
                          $isOpen={dropdownStates.year}
                          $hasValue={!!formData.birthYear}
                        >
                          <span>{formData.birthYear || '연'}</span>
                          <BirthDropdownChevron $isOpen={dropdownStates.year}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </BirthDropdownChevron>
                        </DropdownButton>
                        {dropdownStates.year && (
                          <DropdownMenu>
                            {years.map(year => (
                              <DropdownItem
                                key={year}
                                onClick={() => handleDropdownSelect('year', year.toString())}
                              >
                                {year}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        )}
                      </DropdownContainer>

                      <DropdownContainer>
                        <DropdownButton 
                          type="button"
                          onClick={() => handleDropdownToggle('month')}
                          $isOpen={dropdownStates.month}
                          $hasValue={!!formData.birthMonth}
                        >
                          <span>{formData.birthMonth || '월'}</span>
                          <BirthDropdownChevron $isOpen={dropdownStates.month}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </BirthDropdownChevron>
                        </DropdownButton>
                        {dropdownStates.month && (
                          <DropdownMenu>
                            {months.map(month => (
                              <DropdownItem
                                key={month}
                                onClick={() => handleDropdownSelect('month', month.toString())}
                              >
                                {month}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        )}
                      </DropdownContainer>

                      <DropdownContainer>
                        <DropdownButton 
                          type="button"
                          onClick={() => handleDropdownToggle('day')}
                          $isOpen={dropdownStates.day}
                          $hasValue={!!formData.birthDay}
                        >
                          <span>{formData.birthDay || '일'}</span>
                          <BirthDropdownChevron $isOpen={dropdownStates.day}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <path d="M11.07 6.40L8 9.47L4.93 6.40" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </BirthDropdownChevron>
                        </DropdownButton>
                        {dropdownStates.day && (
                          <DropdownMenu>
                            {days.map(day => (
                              <DropdownItem
                                key={day}
                                onClick={() => handleDropdownSelect('day', day.toString())}
                              >
                                {day}
                              </DropdownItem>
                            ))}
                          </DropdownMenu>
                        )}
                      </DropdownContainer>
                    </>
                  ) : (
                    <>
                      <DisabledBirthInput value={formData.birthYear} readOnly />
                      <DisabledBirthInput value={formData.birthMonth} readOnly />
                      <DisabledBirthInput value={formData.birthDay} readOnly />
                    </>
                  )}
                </BirthContainer>
              </FormGroup>

              {/* 휴대폰 번호 */}
              <FormGroup>
                <FormLabel>휴대폰 번호</FormLabel>
                {loading ? (
                  <SkeletonInput />
                ) : isEditingBasicInfo ? (
                  <FormInput
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                ) : (
                  <DisabledInput value={formData.phone} readOnly />
                )}
              </FormGroup>
            </Section>

            <Divider />

            {/* 회원 자격 인증 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>회원 자격 인증</SectionTitle>
              </SectionHeader>

              {/* 나의 인증 내역 */}
              <VerificationGroup>
                <VerificationLabel>나의 인증 내역</VerificationLabel>
                <VerificationCard>
                  {isLoggedIn === false ? (
                    // 로그인하지 않은 상태 - 인증 대기 중
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.334 10.6673C17.334 11.4037 16.7371 12.0007 16.0007 12.0007C15.2643 12.0007 14.6674 11.4037 14.6674 10.6673C14.6674 9.93094 15.2643 9.33398 16.0007 9.33398C16.7371 9.33398 17.334 9.93094 17.334 10.6673Z" fill="#37383C" fillOpacity="0.61"/>
                          <path d="M17.2008 15.334C17.2008 14.6712 16.6636 14.134 16.0008 14.134C15.3381 14.134 14.8009 14.6712 14.8009 15.334V21.334C14.8009 21.9967 15.3381 22.534 16.0008 22.534C16.6636 22.534 17.2008 21.9967 17.2008 21.334V15.334Z" fill="#37383C" fillOpacity="0.61"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M2.80078 16.0008C2.80078 8.71062 8.71059 2.80078 16.0007 2.80078C23.2908 2.80078 29.2007 8.71062 29.2007 16.0008C29.2007 23.2909 23.2908 29.2008 16.0007 29.2008C8.71059 29.2008 2.80078 23.2909 2.80078 16.0008ZM16.0007 5.20078C10.0361 5.20078 5.20077 10.0361 5.20077 16.0008C5.20077 21.9655 10.0361 26.8008 16.0007 26.8008C21.9654 26.8008 26.8007 21.9655 26.8007 16.0008C26.8007 10.0361 21.9654 5.20078 16.0007 5.20078Z" fill="#37383C" fillOpacity="0.61"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText style={{ color: 'rgba(55, 56, 60, 0.61)' }}>인증 대기 중</VerificationText>
                    </>
                  ) : verificationStatus === 'verified' ? (
                    // 로그인한 상태 - 인증 완료
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21.1821 13.8492C21.6507 13.3806 21.6508 12.6208 21.1822 12.1521C20.7135 11.6835 19.9538 11.6835 19.4851 12.1521L14.3334 17.3036L12.5156 15.4855C12.047 15.0169 11.2872 15.0168 10.8185 15.4854C10.3498 15.954 10.3498 16.7138 10.8184 17.1825L13.4847 19.8491C13.7097 20.0742 14.015 20.2006 14.3332 20.2007C14.6515 20.2007 14.9567 20.0742 15.1818 19.8492L21.1821 13.8492Z" fill="#448181"/>
                          <path fillRule="evenodd" clipRule="evenodd" d="M13.3676 3.23687C14.8152 1.76734 17.1856 1.76744 18.6332 3.2369L20.1172 4.74327C20.3632 4.99292 20.6997 5.13228 21.0501 5.12965L23.1645 5.11383C25.2273 5.09838 26.9033 6.7746 26.8879 8.83725L26.8721 10.9517C26.8695 11.3022 27.009 11.6388 27.2585 11.8846L28.7649 13.3686C30.2344 14.8163 30.2343 17.1867 28.7649 18.6343L27.2585 20.1182C27.0089 20.3642 26.8695 20.7008 26.8721 21.0512L26.8879 23.1657C26.9033 25.2283 25.2272 26.9044 23.1645 26.889L21.0501 26.8732C20.6996 26.8706 20.3631 27.01 20.1171 27.2596L18.6332 28.766C17.1856 30.2355 14.8153 30.2355 13.3676 28.766L11.8835 27.2596C11.6377 27.01 11.3011 26.8706 10.9506 26.8732L8.83616 26.889C6.77352 26.9044 5.09738 25.2284 5.11283 23.1656L5.12865 21.0511C5.13128 20.7008 4.99184 20.3642 4.74219 20.1181L3.2359 18.6343C1.76641 17.1866 1.76642 14.8163 3.2359 13.3686L4.74226 11.8846C4.99189 11.6387 5.13128 11.3021 5.12865 10.9517L5.11283 8.83724C5.09738 6.77449 6.77348 5.09838 8.83623 5.11383L10.9507 5.12965C11.3011 5.13228 11.6377 4.99286 11.8836 4.74323L13.3676 3.23687ZM16.9235 4.92118C16.4159 4.40592 15.5848 4.40601 15.0773 4.92114L13.5932 6.42758C12.8919 7.13951 11.9321 7.53707 10.9327 7.52959L8.81825 7.51376C8.09501 7.50835 7.50734 8.09602 7.51275 8.81928L7.52858 10.9338C7.53606 11.9331 7.13848 12.8929 6.42655 13.5943L4.9202 15.0783C4.40496 15.5859 4.40497 16.417 4.9202 16.9246L6.42662 18.4086C7.13849 19.11 7.53606 20.0697 7.52858 21.0691L7.51275 23.1836C7.50734 23.9067 8.09501 24.4944 8.81832 24.4891L10.9329 24.4732C11.9321 24.4658 12.8919 24.8633 13.5933 25.5753L15.0773 27.0817C15.5849 27.5969 16.416 27.5969 16.9235 27.0817L18.4075 25.5753C19.1089 24.8633 20.0688 24.4658 21.068 24.4732L23.1825 24.4891C23.9057 24.4944 24.4933 23.9067 24.488 23.1835L24.4722 21.069C24.4648 20.0697 24.8623 19.1099 25.5743 18.4085L27.0806 16.9246C27.5958 16.417 27.5958 15.5859 27.0806 15.0783L25.5742 13.5943C24.8622 12.8929 24.4648 11.933 24.4722 10.9338L24.488 8.81926C24.4933 8.09595 23.9056 7.50835 23.1825 7.51376L21.068 7.52959C20.0687 7.53707 19.1088 7.13942 18.4074 6.42754L16.9235 4.92118Z" fill="#448181"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText>의사 · {formData.specialization}</VerificationText>
                    </>
                  ) : verificationStatus === 'pending' ? (
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          <circle cx="16" cy="16" r="14.4" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="3.2"/>
                          <path d="M16 10.667V16" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="2" strokeLinecap="round"/>
                          <circle cx="16" cy="21.333" r="1.333" fill="rgba(55, 56, 60, 0.61)"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText style={{ color: 'rgba(55, 56, 60, 0.61)' }}>인증 대기 중</VerificationText>
                    </>
                  ) : (
                    <>
                      <VerificationIcon>
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                          <circle cx="16" cy="16" r="14.4" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="3.2"/>
                          <path d="M16 10.667V16" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="2" strokeLinecap="round"/>
                          <circle cx="16" cy="21.333" r="1.333" fill="rgba(55, 56, 60, 0.61)"/>
                        </svg>
                      </VerificationIcon>
                      <VerificationText style={{ color: 'rgba(55, 56, 60, 0.61)' }}>인증이 필요합니다</VerificationText>
                    </>
                  )}
                </VerificationCard>
              </VerificationGroup>

              {/* 추가 인증 */}
              <VerificationGroup>
                <VerificationLabel>추가 인증</VerificationLabel>
                <VerificationDescription>
                  기존 인증 내역을 수정·변경 혹은 갱신·최신화하실 수 있습니다. 제출해주신 정보를 바탕으로 관리자 확인을 거쳐 추가 인증이 최종 승인됩니다.
                </VerificationDescription>
                
                {/* 탭 */}
                <VerificationTabs>
                  <VerificationTab 
                    className={activeVerificationTab === 'student' ? 'active' : ''}
                    onClick={() => handleVerificationTabChange('student')}
                  >
                    의대생
                  </VerificationTab>
                  <VerificationTab 
                    className={activeVerificationTab === 'doctor' ? 'active' : ''}
                    onClick={() => handleVerificationTabChange('doctor')}
                  >
                    의사
                  </VerificationTab>
                </VerificationTabs>

                {/* 의사 탭 선택 시 면허민원 안내 */}
                {activeVerificationTab === 'doctor' && (
                  <DoctorVerificationNote>
                    투비닥터 캠퍼스는 보건복지부 면허민원 서비스를 통해 면허번호 인증 절차를 진행합니다.
                  </DoctorVerificationNote>
                )}

                {/* 입력 필드 */}
                {activeVerificationTab === 'student' ? (
                  <FormGroup>
                    <FormSelect 
                      name="department"
                      value={identityFormData.department} 
                      onChange={handleIdentityInputChange}
                    >
                      <option value="">소속 대학을 선택해주세요.</option>
                      <option value="고려대학교">고려대학교</option>
                      <option value="연세대학교">연세대학교</option>
                      <option value="서울대학교">서울대학교</option>
                      <option value="성균관대학교">성균관대학교</option>
                      <option value="가톨룩대학교">가톨룩대학교</option>
                      <option value="부산대학교">부산대학교</option>
                      <option value="경상대학교">경상대학교</option>
                      <option value="전남대학교">전남대학교</option>
                      <option value="대구가톨룩대학교">대구가톨룩대학교</option>
                    </FormSelect>
                  </FormGroup>
                ) : (
                  <DoctorFormGroup>
                    <FormGroup>
                      <FormSelect
                        name="department"
                        value={identityFormData.department}
                        onChange={handleIdentityInputChange}
                      >
                        <option value="">진료과를 선택해주세요.</option>
                        <option value="가정의학과">가정의학과</option>
                        <option value="내과">내과</option>
                        <option value="외과">외과</option>
                        <option value="소아청소년과">소아청소년과</option>
                        <option value="산부인과">산부인과</option>
                        <option value="정신건강의학과">정신건강의학과</option>
                        <option value="안과">안과</option>
                        <option value="이비인후과">이비인후과</option>
                        <option value="피부과">피부과</option>
                        <option value="비뇨기과">비뇨기과</option>
                        <option value="수순과">서순과</option>
                        <option value="신경외과">신경외과</option>
                        <option value="내분비대사내과">내분비대사내과</option>
                        <option value="직업환경의학과">직업환경의학과</option>
                        <option value="예방의학과">예방의학과</option>
                        <option value="전공의">전공의</option>
                        <option value="수련의">수련의</option>
                        <option value="기타">기타</option>
                      </FormSelect>
                    </FormGroup>
                    
                    <FormGroup>
                      <FormInput
                        type="text"
                        name="licenseNumber"
                        value={identityFormData.licenseNumber}
                        onChange={handleIdentityInputChange}
                        placeholder="면허번호를 입력해주세요."
                      />
                    </FormGroup>
                  </DoctorFormGroup>
                )}

                {/* 이미지 업로드 (의대생일 때만) */}
                {activeVerificationTab === 'student' && (
                  <UploadSection>
                    <UploadDescription>
                      이름, 소속 대학, 학번이 식별 가능한 학생증 혹은 재학증명서 이미지를 업로드해주세요.
                      {identityFormData.selectedFile && (
                        <SelectedFileName>
                          선택된 파일: {identityFormData.selectedFile.name}
                        </SelectedFileName>
                      )}
                    </UploadDescription>
                    <UploadButton onClick={() => identityFileInputRef.current?.click()}>
                      이미지 업로드
                    </UploadButton>
                    <HiddenFileInput
                      ref={identityFileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      onChange={handleIdentityFileChange}
                    />
                  </UploadSection>
                )}

                {/* 제출 버튼 */}
                <SubmitButton 
                  disabled={
                    isSubmittingIdentity ||
                    (activeVerificationTab === 'student' 
                      ? !identityFormData.department || !identityFormData.selectedFile
                      : !identityFormData.department || !identityFormData.licenseNumber)
                  }
                  active={
                    !isSubmittingIdentity &&
                    (activeVerificationTab === 'student' 
                      ? !!(identityFormData.department && identityFormData.selectedFile)
                      : !!(identityFormData.department && identityFormData.licenseNumber))
                  }
                  onClick={handleIdentitySubmit}
                >
                  {isSubmittingIdentity ? '제출 중...' : '제출하기'}
                </SubmitButton>
              </VerificationGroup>
            </Section>

            <Divider />

            {/* 마케팅 수신 동의 섹션 */}
            <Section>
              <SectionHeader>
                <SectionTitle>마케팅 수신 동의</SectionTitle>
                <ToggleSwitch 
                  active={marketingConsent}
                  onClick={() => setMarketingConsent(!marketingConsent)}
                >
                  <ToggleSlider active={marketingConsent} />
                </ToggleSwitch>
              </SectionHeader>

              <MarketingText>
                마케팅 정보 수신에 동의합니다. (추후 이부분 멘트 구체화 필요)
              </MarketingText>
            </Section>
          </MainContent>
        </MainContainer>
      </Container>
      
      {/* 기본정보 수정 모달 */}
      <BasicInfoEditModal
        isOpen={isBasicInfoModalOpen}
        onClose={() => setIsBasicInfoModalOpen(false)}
        onComplete={handleBasicInfoUpdate}
        initialData={userInfo ? {
          name: userInfo.name || '',
          birth: userInfo.birth || '',
          phone: userInfo.phone || ''
        } : undefined}
      />
    </MyPageLayout>
  );
};// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 32px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationContainer = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 32px;
`;

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const BreadcrumbItem = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.09px;
`;

const BreadcrumbChevron = styled.div`
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MainContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 48px;
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const SectionHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  color: black;
  font-size: 28px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 38px;
  margin: 0;
  flex: 1;
`;

const EditButton = styled.button<{ active?: boolean }>`
  padding: 9px 20px;
  border-radius: 10px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  color: #448181;
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0.14px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 40px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ProfileImageSection = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const ProfileImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ProfileImageWrapper = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: white;
  outline: 1px solid rgba(112, 115, 124, 0.08);
  outline-offset: -1px;
  position: relative;
  /* overflow 제거하여 편집 버튼이 밖으로 나올 수 있게 함 */
  transition: opacity 0.2s ease;
  
  &:hover .overlay {
    /* opacity: 1; */
  }
`;

const ProfileImageOverlay = styled.div.attrs({
  className: 'overlay'
})`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const DeleteImageButton = styled.button<{ disabled?: boolean }>`
  padding: 4px 12px;
  background: transparent;
  border: 1px solid rgba(220, 38, 38, 0.3);
  border-radius: 6px;
  color: rgba(220, 38, 38, 0.8);
  font-size: 12px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.disabled ? 'transparent' : 'rgba(220, 38, 38, 0.05)'};
    border-color: ${props => props.disabled ? 'rgba(220, 38, 38, 0.3)' : 'rgba(220, 38, 38, 0.5)'};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProfileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const EditIconButton = styled.button<{ disabled?: boolean }>`
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 24px;
  height: 24px;
  background: #448181;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  z-index: 10;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: ${props => props.disabled ? 0.6 : 0.8};
  }
`;

const FormGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 10px;
`;

const FormLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  outline: none;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  
  &:focus {
    border-color: #448181;
  }
`;

const DisabledInput = styled.input`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  background: #F4F4F5;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  cursor: not-allowed;
`;

const BirthDateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// 생년월일 드롭다운 스타일 (회원가입과 동일)
const BirthContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
`;

const DropdownContainer = styled.div`
  flex: 1;
  position: relative;
`;

const DropdownButton = styled.button<{ $isOpen?: boolean; $hasValue?: boolean }>`
  width: 100%;
  height: 48px;
  padding: 12px;
  border: ${props => props.$isOpen ? '2px solid #448181' : '1px solid rgba(112, 115, 124, 0.16)'};
  border-radius: 12px;
  background: white;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;

  span {
    color: ${props => props.$hasValue ? '#171719' : 'rgba(55, 56, 60, 0.28)'};
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
`;

const BirthDropdownChevron = styled.div<{ $isOpen?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  
  svg {
    color: rgba(55, 56, 60, 0.61);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  margin-top: 4px;
`;

const DropdownItem = styled.div`
  padding: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  color: #171719;
  
  &:hover {
    background-color: rgba(68, 129, 129, 0.05);
  }
  
  &:first-child {
    border-radius: 12px 12px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 12px 12px;
  }
`;

const BirthSelect = styled.select`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  background: white;
  cursor: pointer;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
`;

const DisabledBirthInput = styled.input`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.08);
  background: #F4F4F5;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: #171719;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  cursor: not-allowed;
`;

const ChangeButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: transparent;
  color: #448181;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  height: 48px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const PasswordChangeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const PasswordHint = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
`;

const PasswordChangeButton = styled.button<{ active: boolean }>`
  width: 100%;
  padding: 12px 28px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#448181' : '#F4F4F5'};
  color: ${props => props.active ? 'white' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: ${props => props.active ? 'pointer' : 'not-allowed'};
  
  &:hover {
    opacity: ${props => props.active ? 0.8 : 1};
  }
`;

const DoctorVerificationNote = styled.div`
  width: 100%;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
  text-align: center;
  padding: 0 10px;
`;

const DoctorFormGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background: rgba(112, 115, 124, 0.22);
`;

const VerificationGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const VerificationLabel = styled.div`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
  text-align: center;
`;

const VerificationCard = styled.div`
  width: 100%;
  height: 172px;
  padding: 48px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const VerificationIcon = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const VerificationText = styled.div`
  color: #171719;
  font-size: 20px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 28px;
`;

const VerificationDescription = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const VerificationTabs = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 16px;
  position: relative;
`;

const VerificationTab = styled.button`
  flex: 1;
  padding: 10px 0;
  text-align: center;
  cursor: pointer;
  background-color: white;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 17px;
  font-weight: 500;
  color: rgba(55, 56, 60, 0.61);
  position: relative;
  z-index: 1;
  
  &:first-child {
    border-radius: 12px 0 0 12px;
  }
  
  &:last-child {
    border-radius: 0 12px 12px 0;
    margin-left: -1px;
  }
  
  &.active {
    border-color: #448181;
    color: #448181;
    background-color: rgba(68, 129, 129, 0.05);
    z-index: 2;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  color: rgba(55, 56, 60, 0.28);
  background: white;
  cursor: pointer;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.03);
  
  &:focus {
    border-color: #448181;
    color: #171719;
  }
`;

const UploadSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const UploadDescription = styled.div`
  flex: 1;
  color: rgba(55, 56, 60, 0.61);
  font-size: 13px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.25px;
`;

const SelectedFileName = styled.div`
  margin-top: 8px;
  color: #448181;
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 16px;
`;

const UploadButton = styled.button`
  padding: 12px 28px;
  border-radius: 12px;
  border: 1px solid #448181;
  background: transparent;
  color: #448181;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.8;
  }
`;

const SubmitButton = styled.button<{ disabled?: boolean; active?: boolean }>`
  width: 100%;
  padding: 12px 28px;
  border-radius: 12px;
  border: none;
  background: ${props => props.active && !props.disabled ? '#448181' : '#F4F4F5'};
  color: ${props => props.active && !props.disabled ? 'white' : 'rgba(55, 56, 60, 0.28)'};
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.09px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  
  &:hover {
    opacity: ${props => props.disabled ? 1 : 0.8};
  }
`;

const ToggleSwitch = styled.button<{ active: boolean }>`
  width: 52px;
  height: 32px;
  padding: 4px;
  background: ${props => props.active ? '#448181' : 'rgba(112, 115, 124, 0.16)'};
  border: none;
  border-radius: 100px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ToggleSlider = styled.div<{ active: boolean }>`
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  transition: transform 0.2s ease;
  transform: ${props => props.active ? 'translateX(20px)' : 'translateX(0)'};
`;

const MarketingText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const LoadingContainer = styled.div`
  width: 100%;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LoadingText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
`;

// 스켈레톤 애니메이션
const shimmer = `
  @keyframes shimmer {
    0% {
      background-position: -468px 0;
    }
    100% {
      background-position: 468px 0;
    }
  }
`;

const SkeletonBase = styled.div`
  ${shimmer}
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
  border-radius: 12px;
`;

const SkeletonInput = styled(SkeletonBase)`
  height: 48px;
  width: 100%;
`;

const SkeletonBirthInput = styled(SkeletonBase)`
  height: 48px;
  flex: 1;
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 40px;
  width: 80px;
`;

const SkeletonCircle = styled(SkeletonBase)`
  width: 64px;
  height: 64px;
  border-radius: 50%;
`;

export default MyPageProfileEdit;